import { Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useAlert } from '../../../../AlertProvider';

import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CancelSessionOutput, patientIsMedicaid } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useAppointmentQuery, useChangeSessionReasonsQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { ChangeSessionReasonTypes, UserRole } from '@uplift/types';
import { lateFeeAmountByAppointmentType } from '@uplift/utils';

import { Alert } from '../../../../Alert';
import { Button } from '../../../../Button';
import { RadioControl, TextInput } from '../../../../Inputs';
import { Loader } from '../../../../Loader';
import {
  ModalFullPage, ModalFullPageActions, ModalFullPageContent, ModalFullPageTitle, ModalFullPageType
} from '../../../../ModalFullPage';
import { formatCents } from '../../../../Money';
import { checkShowLateFee } from '../helpers';
import { useCancelModalStyles } from '../styles';
import { CancelFormModalCommonProps, ModalStep } from '../types';
import {
  useCancelSessionForm, UseCancelSessionFormArgs, useCancelSessionFormValidationSchema
} from '../use-cancel-session-form.hook';
import { NextAction } from './next-action';
import { Subtitle } from './subtitle';

export type PatientCancelSessionModalProps =
  & CancelFormModalCommonProps
  & Pick<ModalFullPageType, 'onClose'>
  & {
    open: boolean;
    providerName: string;
    patientId: string;
    modalTitleOverride?: string;
    onSubmit?: (response: CancelSessionOutput) => Promise<void>;
    isMaterialized?: boolean;
  };

export const PatientCancelSessionModal: React.VFC<PatientCancelSessionModalProps> = ({
  open,
  cancelMode = 'manual',
  appointmentId,
  fromDatetime,
  parentId,
  patientId,
  modalTitleOverride,
  providerName,
  onClose,
  isMaterialized,
  ...props
}) => {
  const l10n = useL10n();
  const validationSchema = useCancelSessionFormValidationSchema(l10n);
  const alert = useAlert();
  const { classes } = useCancelModalStyles();
  const timezone = useTimezone();
  const { data: appointment } = useAppointmentQuery((parentId && fromDatetime && !isMaterialized) ? {
    id: appointmentId,
    parentId: parentId,
    fromDatetime: fromDatetime,
    isMaterialized: false,
  } : {
    id: appointmentId,
    isMaterialized: true,
  });
  const { reasons } = useChangeSessionReasonsQuery(UserRole.patient, ChangeSessionReasonTypes.cancel);
  const [ step, setStep ] = useState<ModalStep>(() => {
    if (!parentId) {
      return 'reason';
    }

    return [ 'single', 'all' ].includes(cancelMode) ? 'reason' : 'session';
  });
  const showLateFeeWarning = useMemo(() => checkShowLateFee(fromDatetime), [ fromDatetime ]);

  const appointmentDate = useMemo(() => CalendarDateService.date(fromDatetime).tz(timezone), [ fromDatetime, timezone ]);

  const { data, isFetching } = useQuery({
    queryKey: [ 'insurance', patientId, 'medicaid' ],
    queryFn: () => patientIsMedicaid(patientId),
    enabled: !!patientId
  });

  const isMedicaid = data?.data;

  const onSuccess: UseCancelSessionFormArgs['onSuccess'] = useCallback(async (data, formValues) => {
    await props?.onSubmit?.(data);

    const alertMessage = {
      all: l10n.sessions.successfullyCanceledAll,
      single: l10n.sessions.successfullyCanceled,
    }[formValues.recurring];

    alert.success(alertMessage);
    onClose?.();
  }, [ alert, l10n.sessions, onClose, props ]);

  const onError: UseCancelSessionFormArgs['onError'] = useCallback(() => {
    alert.error(l10n.sessions.cancelError);

    onClose?.();
  }, [ alert, l10n.sessions.cancelError, onClose ]);

  const { onSubmit, initialValues } = useCancelSessionForm({
    cancelMode,
    appointmentId,
    fromDatetime,
    parentId,
    onSuccess,
    onError
  });

  const handleNextStep = useCallback(() => {
    switch (step) {
      case 'session':
        setStep('reason');
        break;
      case 'reason':
        setStep('note');
        break;
      default:
        break;
    }
  }, [ step ]);

  const modalTitle = useMemo(() => {
    if (modalTitleOverride) {
      return modalTitleOverride;
    }

    const date = appointmentDate.format('MMM D');
    const time = appointmentDate.format('h:mm a');

    if (step === 'session') {
      return l10n.schedule.cancelAppointment.sessionWith(providerName);
    }

    if (step === 'reason') {
      if (cancelMode === 'all') {
        return l10n.schedule.cancelAppointment.allWith(providerName);
      }

      return l10n.schedule.cancelAppointment.sessionAtWith(date, time, providerName);
    }

    const text = cancelMode === 'all'
      ? l10n.schedule.cancelAppointment.allWith(providerName)
      : l10n.schedule.cancelAppointment.sessionAtWith(date, time, providerName);

    return (
      <Typography variant="h6">
        {text}
      </Typography>
    );
  }, [ modalTitleOverride, appointmentDate, step, cancelMode, l10n.schedule.cancelAppointment, providerName ]);

  const content = useMemo(() => {
    if (isFetching) {
      return <Loader />;
    }

    switch (step) {
      case 'session':
        return (
          <RadioControl
            name="recurring"
            label={l10n.schedule.cancelAppointment.whatToCancel}
            values={[
              { label: `${appointmentDate.format('ddd, MMM D')} at ${appointmentDate.format('h:mm a')}`, value: 'single' },
              { label: l10n.schedule.cancelAppointment.allSessions, value: 'all' }
            ]}
          />
        );
      case 'reason':
        return (
          <RadioControl
            name="cancelationReason"
            label={l10n.schedule.cancelAppointment.whyCancel}
            values={reasons.map(reason => ({
              label: reason.description ?? '',
              value: reason.id
            }))}
          />
        );
      case 'note':
        return (
          <TextInput
            name="cancelNote"
            label={l10n.schedule.cancelAppointment.why}
            multiline
            fullWidth
            rows={5}
          />
        );
      default:
        return null;
    }
  }, [ appointmentDate, l10n.schedule.cancelAppointment, reasons, step, isFetching ]);

  const lateFeeText = isMedicaid
    ? l10n.schedule.cancelAppointment.feeWarningMedicaid
    : l10n.schedule.cancelAppointment.feeWarning(formatCents(appointment?.type ? lateFeeAmountByAppointmentType[appointment.type] : 0));

  return (
    <ModalFullPage
      open={open}
      transition
      mobileOnly="sm"
      onClose={onClose}
    >
      {({ displayMobileOnlyStyles }) => (
        <Formik
          key={open ? 'opened' : 'closed'}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          isInitialValid={false}
        >
          {(formik) => (
            <>
              <ModalFullPageTitle displayMobileOnlyStyles={!!displayMobileOnlyStyles} component={step === 'note' ? 'span' : 'h2'}>
                <Box display="flex" flexDirection="column" gap=".5rem">
                  {modalTitle}
                  <Subtitle step={step} reason={formik.values.cancelationReason} />
                </Box>
              </ModalFullPageTitle>
              <ModalFullPageContent className={classes.content}>
                <Box display="flex" flexDirection="column" gap="1rem" px={1}>
                  {showLateFeeWarning && (
                    <Alert severity="warning">
                      {lateFeeText}
                    </Alert>
                  )}
                  {content}
                </Box>
              </ModalFullPageContent>
              <ModalFullPageActions displayMobileOnlyStyles={displayMobileOnlyStyles}>
                <Button variant="text" onClick={onClose}>
                  {formik.values.recurring === 'all'
                    ? l10n.schedule.cancelAppointment.keepAllSessions
                    : l10n.schedule.cancelAppointment.keepSession}
                </Button>
                <NextAction step={step} formik={formik} onNextStep={handleNextStep} />
              </ModalFullPageActions>
            </>
          )}
        </Formik>
      )}
    </ModalFullPage>
  );
};
